import { TechnicalRatingAction } from '../../../util/technicals'

export type RatingSnapshot = {
    value: number
    previous?: number
    previousPrevious?: number
    extras: Record<string, number>
    uptrend?: boolean | null
}

const t = (thresholds: Record<string, number>, key: string, fallback: number) =>
    thresholds[key] ?? fallback

export const computeUptrend = (close: number[], smaLength: number): boolean | null => {
    if (close.length < smaLength) return null
    let sum = 0
    for (let i = close.length - smaLength; i < close.length; i++) {
        sum += close[i]
    }
    const sma = sum / smaLength
    return close[close.length - 1] > sma
}

export const rateIndicator = (
    ratingStyle: string,
    s: RatingSnapshot,
    close: number | undefined,
    thresholds: Record<string, number>
): TechnicalRatingAction => {
    switch (ratingStyle) {
        case 'RSI': {
            const prev = s.previous
            if (prev === undefined) return 'Neutral'
            const oversold = t(thresholds, 'rsi.oversold', 30)
            const overbought = t(thresholds, 'rsi.overbought', 70)
            if (s.value < oversold && s.value > prev) return 'Buy'
            if (s.value > overbought && s.value < prev) return 'Sell'
            return 'Neutral'
        }
        case 'STOCHASTIC': {
            const d = s.extras['%D']
            if (d === undefined) return 'Neutral'
            const oversold = t(thresholds, 'stoch.oversold', 20)
            const overbought = t(thresholds, 'stoch.overbought', 80)
            const k = s.value
            if (k < oversold && d < oversold && k > d) return 'Buy'
            if (k > overbought && d > overbought && k < d) return 'Sell'
            return 'Neutral'
        }
        case 'CCI': {
            const prev = s.previous
            if (prev === undefined) return 'Neutral'
            const oversold = t(thresholds, 'cci.oversold', -100)
            const overbought = t(thresholds, 'cci.overbought', 100)
            if (s.value < oversold && s.value > prev) return 'Buy'
            if (s.value > overbought && s.value < prev) return 'Sell'
            return 'Neutral'
        }
        case 'ADX': {
            const prev = s.previous
            const plusDi = s.extras.PLUS_DI
            const minusDi = s.extras.MINUS_DI
            if (prev === undefined || plusDi === undefined || minusDi === undefined) return 'Neutral'
            const trend = t(thresholds, 'adx.trend', 20)
            if (plusDi > minusDi && s.value > trend && s.value > prev) return 'Buy'
            if (plusDi < minusDi && s.value > trend && s.value < prev) return 'Sell'
            return 'Neutral'
        }
        case 'AWESOME_OSCILLATOR': {
            const prev = s.previous
            const prev2 = s.previousPrevious
            if (prev === undefined) return 'Neutral'
            const crossOver = prev <= 0 && s.value > 0
            const crossUnder = prev >= 0 && s.value < 0
            const saucerBuy =
                prev2 !== undefined && s.value > 0 && prev > 0 && s.value > prev && prev < prev2
            const saucerSell =
                prev2 !== undefined && s.value < 0 && prev < 0 && s.value < prev && prev > prev2
            if (crossOver || saucerBuy) return 'Buy'
            if (crossUnder || saucerSell) return 'Sell'
            return 'Neutral'
        }
        case 'MOMENTUM': {
            const prev = s.previous
            if (prev === undefined) return 'Neutral'
            if (s.value > prev) return 'Buy'
            if (s.value < prev) return 'Sell'
            return 'Neutral'
        }
        case 'MACD': {
            const signal = s.extras.signal
            if (signal === undefined) return 'Neutral'
            if (s.value > signal) return 'Buy'
            if (s.value < signal) return 'Sell'
            return 'Neutral'
        }
        case 'STOCH_RSI': {
            const d = s.extras['%D']
            if (d === undefined || s.uptrend == null) return 'Neutral'
            const oversold = t(thresholds, 'stoch.oversold', 20)
            const overbought = t(thresholds, 'stoch.overbought', 80)
            const k = s.value
            if (!s.uptrend && k < oversold && d < oversold && k > d) return 'Buy'
            if (s.uptrend && k > overbought && d > overbought && k < d) return 'Sell'
            return 'Neutral'
        }
        case 'WILLIAMS_R': {
            const prev = s.previous
            if (prev === undefined) return 'Neutral'
            const oversold = t(thresholds, 'williams.oversold', -80)
            const overbought = t(thresholds, 'williams.overbought', -20)
            if (s.value < oversold && s.value > prev) return 'Buy'
            if (s.value > overbought && s.value < prev) return 'Sell'
            return 'Neutral'
        }
        case 'BULL_BEAR_POWER': {
            const bull = s.extras.BULL
            const bear = s.extras.BEAR
            const prevBear = s.extras.PREV_BEAR
            const prevBull = s.extras.PREV_BULL
            if (bull === undefined || bear === undefined || s.uptrend == null) return 'Neutral'
            if (s.uptrend && bear < 0 && prevBear !== undefined && bear > prevBear) return 'Buy'
            if (!s.uptrend && bull > 0 && prevBull !== undefined && bull < prevBull) return 'Sell'
            return 'Neutral'
        }
        case 'ULTIMATE_OSCILLATOR': {
            const oversold = t(thresholds, 'uo.oversold', 30)
            const overbought = t(thresholds, 'uo.overbought', 70)
            if (s.value > overbought) return 'Buy'
            if (s.value < oversold) return 'Sell'
            return 'Neutral'
        }
        case 'ICHIMOKU': {
            const price = close
            if (price === undefined) return 'Neutral'
            const conversion = s.extras.CONVERSION_LINE
            const base = s.extras.BASE_LINE
            const spanA = s.extras.LEADING_SPAN_A
            const spanB = s.extras.LEADING_SPAN_B
            if (
                conversion === undefined ||
                base === undefined ||
                spanA === undefined ||
                spanB === undefined
            ) {
                return 'Neutral'
            }
            const buy = spanA > spanB && base > spanA && conversion > base && price > conversion
            const sell = spanA < spanB && base < spanA && conversion < base && price < conversion
            if (buy) return 'Buy'
            if (sell) return 'Sell'
            return 'Neutral'
        }
        case 'MA_VS_CLOSE': {
            if (close === undefined) return 'Neutral'
            if (close > s.value) return 'Buy'
            if (close < s.value) return 'Sell'
            return 'Neutral'
        }
        default:
            return 'Neutral'
    }
}
